import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Edit, User, UserRoundCog } from 'lucide-react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { Link } from 'react-router-dom'
import { groupBy } from 'lodash'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { Badge } from '@/components/ui/badge'
import { useVisibleTeamsForUser } from 'app/frontend/base/helpers/hooks'

const TeamNavigationMenu = () => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  const filteredAndSortedTeamReferences = useVisibleTeamsForUser(user)

  if (!user) return null

  const teamsByOwner = groupBy(
    filteredAndSortedTeamReferences,
    (teamReference) =>
      `${teamReference.ownerReference.id}-${teamReference.ownerType}`,
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className='flex gap-1 items-center cursor-pointer mr-2'>
          <span className='text-sm'>{t(`header.teams`)}</span>
          <ChevronDown
            width={12}
            className='ml-0.5 mt-1 opacity-70 hidden max-[420px]:block sm:block'
          />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {Object.values(teamsByOwner).map((teamReferences, i) => {
          const owner = teamReferences[0].ownerReference

          return (
            <div key={i}>
              {i > 0 ? <DropdownMenuSeparator /> : null}

              <Link to={owner.path}>
                <DropdownMenuItem>
                  <div className='flex items-center gap-2'>
                    <BannerImage
                      faction={owner?.faction}
                      fileName={owner?.bannerFilename}
                      size='small'
                    />
                    <div>
                      {owner.name}

                      <div className='opacity-70 font-normal text-[0.7rem] mt-[-0.2rem]'>
                        {owner.realm.name} (<span>{owner.realm.region}</span>)
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              </Link>

              {teamReferences.map((teamReference) => (
                <Link to={teamReference.path} key={teamReference.id}>
                  <DropdownMenuItem className='pl-4 flex gap-4 justify-between items-center'>
                    <span>{teamReference.name}</span>
                    <Badge
                      variant='outline'
                      className='flex items-center gap-1'
                    >
                      <span>{teamReference.characterAmount}</span>
                      <User size={12} />
                    </Badge>
                  </DropdownMenuItem>
                </Link>
              ))}
            </div>
          )
        })}

        {user.teamReferences.length === 0 ? (
          <>
            <DropdownMenuLabel className='text-center font-normal max-w-[20rem]'>
              {t('header.no-teams')}
            </DropdownMenuLabel>
            {window.location.pathname !== '/profile/characters' && (
              <>
                <DropdownMenuSeparator />
                <Link to='/profile/characters'>
                  <DropdownMenuItem className='w-full flex items-center justify-center gap-2'>
                    <UserRoundCog size={14} /> {t('header.profile')}
                  </DropdownMenuItem>
                </Link>
              </>
            )}
          </>
        ) : (
          <>
            {filteredAndSortedTeamReferences.length === 0 ? (
              <DropdownMenuLabel className='text-center font-normal'>
                ...
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuSeparator />
            <Link to='/profile/settings#teams'>
              <DropdownMenuItem>
                <span className='flex gap-2 items-center text-muted-foreground'>
                  <Edit width={14} />
                  Edit visible teams
                </span>
              </DropdownMenuItem>
            </Link>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TeamNavigationMenu
